import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { noop } from 'lodash';

import EditOffIcon from '@mui/icons-material/EditOff';
import EditIcon from '@mui/icons-material/Edit';

import {
  ResolvableModeEnum,
  ServiceUsagesSourceEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { AlertMessages } from '../../components/AlertMessages';
import { PricingEditionContextProvider } from '../../contexts/PricingEditionContext';
import { hasSubMissions } from '../../app/utils/mission-kind-profile.util';
import { ServiceUsagesGroups } from '../../components/ServiceUsages/Groups';
import { FrozenServiceUsagesGroups } from '../../components/ServiceUsages/FrozenGroups';
import { SpeedDial } from '../../components/SpeedDial';
import { MissionResolvableServiceUsagesFormStep } from '../../forms/MissionServicesInfo';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type { ResolvableServiceUsagePaths } from '../../forms/ResolvableServiceUser/types/paths.type';

import { usePricingEditionPermission } from './hooks/usePricingEditionPermission.hook';
import { useServiceUsagesConsolidationAndCalculation } from './hooks/useServiceUsagesConsolidationAndCalculation.hook';
import {
  pricedGroupsOf,
  useSubMissionsPricing,
} from './hooks/useSubMissionsPricing.hook';
import { COLLABORATION_SERVICES_LABEL } from './constants/collaboration-pricing.constant';
import { FrozenSubMissionsPricing } from './FrozenSubMissionsPricing';
import { SubMissionsPricing } from './SubMissionsPricing';
import { buildGroupTotalCalculationMap } from '../../utils/build-group-total-calculation-map.util';
import { useOptionGroupsSelection } from './hooks/useOptionGroupsSelection.hook';
import { combineOptionGroupsChoices } from '../../utils/option-groups-choices.util';
import { splitSnapshotGroupsBySubMission } from './utils/split-snapshot-groups-by-sub-mission.util';
import { buildFrozenTotalSections } from './utils/frozen-total-sections.util';
import { isPricingEditionModeAvailable } from './utils/pricing-edition-mode.util';

interface Props {
  readonly paths?: ResolvableServiceUsagePaths;
}

const RESOLVABLE_FIELD_SETTINGS = {
  enableDirectGeneration: true,
  primaryMode: ResolvableModeEnum.Static,
};

export const ServiceUsages = ({ paths }: Readonly<Props>) => {
  const { pathname: currentPath } = useLocation();

  const isInEditionMode = !!paths && currentPath.startsWith(paths.edit);

  const { mission } = useMissionReadyContext();

  const {
    fallbackServiceUsagesGroups,
    groupTotalCalculations,
    isOptionGroupsSelectionEditable,
    liveGroupTotalCalculations,
    liveTotalCalculation,
    selectedOptionGroupIds,
    serviceUsagesGroups,
    snapshotGroupTotalCalculations,
    snapshotSelectedOptionGroupIds,
    snapshotServiceUsagesGroups,
    totalCalculation,
    source,
  } = useServiceUsagesConsolidationAndCalculation();

  const isFromServicesInfo = source === ServiceUsagesSourceEnum.ServicesInfo;

  const { subMissions, subMissionGroups, subMissionTotalSections } =
    useSubMissionsPricing(mission._id, mission.kind);
  const isComposite = hasSubMissions(mission.kind);
  const ownGroups = pricedGroupsOf({
    serviceUsagesGroups,
    fallbackServiceUsagesGroups,
  });
  const totalSections = isComposite
    ? [
        ...subMissionTotalSections,
        {
          _id: mission._id,
          label: COLLABORATION_SERVICES_LABEL,
          groupIds: ownGroups.map((group) => group._id),
        },
      ]
    : undefined;
  // A collaboration shows the choice of its groups with the ones of its
  // sub-missions, each mission keeping the choice of its own groups.
  const effectiveSelectedOptionGroupIds = isComposite
    ? combineOptionGroupsChoices([
        ...subMissions.map(({ pricing }) => ({
          selectedOptionGroupIds: pricing.selectedOptionGroupIds,
          groups: pricedGroupsOf(pricing),
        })),
        { selectedOptionGroupIds, groups: ownGroups },
      ])
    : selectedOptionGroupIds;

  // The option choice is only changed in the edition mode.
  const optionGroupsSelection = useOptionGroupsSelection({
    isEditable: isInEditionMode && isOptionGroupsSelectionEditable,
    missionId: mission._id,
  });

  // A frozen pricing is the one of the quotation snapshot: the groups of the
  // sub-missions of a collaboration, then its own groups.
  const frozenPricing = splitSnapshotGroupsBySubMission(
    snapshotServiceUsagesGroups ?? EMPTY_ARRAY,
  );
  const frozenSubMissionGroups = frozenPricing.subMissionSections.flatMap(
    ({ groups }) => groups,
  );

  const pricingEditionPermission = usePricingEditionPermission(mission._id);

  const missionForEditing = {
    ...mission,
    servicesInfo: { serviceUsagesGroups },
  };

  return (
    <PricingEditionContextProvider
      isEditable={pricingEditionPermission.isEditable}
    >
      {!!pricingEditionPermission.reasons.length && (
        <AlertMessages
          messages={pricingEditionPermission.reasons}
          severity="info"
          sx={{ mb: 2 }}
        />
      )}
      {!isInEditionMode ? (
        isFromServicesInfo ? (
          <ServiceUsagesGroups
            additionalTotalGroups={subMissionGroups}
            consultResolvableServiceInNewTab={false}
            editionMode={false}
            fallbackServiceUsagesGroups={fallbackServiceUsagesGroups}
            groupSectionLabel={
              isComposite ? COLLABORATION_SERVICES_LABEL : undefined
            }
            groupTotalCalculations={groupTotalCalculations}
            isInResolvableMode
            leadingContent={
              isComposite ? (
                <SubMissionsPricing
                  groupTotalCalculationMap={buildGroupTotalCalculationMap(
                    groupTotalCalculations,
                  )}
                  isEditionMode={false}
                  subMissions={subMissions}
                />
              ) : undefined
            }
            optionGroupsSelection={optionGroupsSelection}
            resolvableFieldSettings={RESOLVABLE_FIELD_SETTINGS}
            selectedOptionGroupIds={effectiveSelectedOptionGroupIds}
            serviceUsagesGroups={serviceUsagesGroups}
            totalCalculation={totalCalculation}
            totalSections={totalSections}
          />
        ) : (
          <FrozenServiceUsagesGroups
            additionalTotalGroups={frozenSubMissionGroups}
            consultResolvableServiceInNewTab={false}
            groupSectionLabel={
              isComposite ? COLLABORATION_SERVICES_LABEL : undefined
            }
            groupTotalCalculations={snapshotGroupTotalCalculations}
            groups={frozenPricing.ownGroups}
            leadingContent={
              isComposite ? (
                <FrozenSubMissionsPricing
                  groupTotalCalculations={snapshotGroupTotalCalculations}
                  sections={frozenPricing.subMissionSections}
                  selectedOptionGroupIds={snapshotSelectedOptionGroupIds}
                  subMissions={subMissions}
                />
              ) : undefined
            }
            selectedOptionGroupIds={snapshotSelectedOptionGroupIds}
            totalCalculation={totalCalculation}
            totalSections={
              isComposite
                ? buildFrozenTotalSections({
                    collaborationId: mission._id,
                    ownGroups: frozenPricing.ownGroups,
                    subMissionSections: frozenPricing.subMissionSections,
                    subMissions,
                  })
                : undefined
            }
          />
        )
      ) : (
        <Formik
          enableReinitialize
          initialValues={missionForEditing}
          onSubmit={noop}
        >
          <MissionResolvableServiceUsagesFormStep
            additionalTotalGroups={subMissionGroups}
            fallbackResolvableServiceUsagesGroups={fallbackServiceUsagesGroups}
            groupSectionLabel={
              isComposite ? COLLABORATION_SERVICES_LABEL : undefined
            }
            groupTotalCalculations={liveGroupTotalCalculations}
            id="ServiceUsages"
            leadingContent={
              isComposite ? (
                <SubMissionsPricing
                  groupTotalCalculationMap={buildGroupTotalCalculationMap(
                    liveGroupTotalCalculations,
                  )}
                  isEditionMode
                  subMissions={subMissions}
                />
              ) : undefined
            }
            optionGroupsSelection={optionGroupsSelection}
            paths={paths}
            selectedOptionGroupIds={effectiveSelectedOptionGroupIds}
            totalCalculation={liveTotalCalculation}
            totalSections={totalSections}
            useDirectMutation
          />
        </Formik>
      )}
      {!!paths && (
        <SpeedDial
          actions={
            isInEditionMode
              ? [
                  {
                    id: 'leaveEditionMode',
                    icon: <EditOffIcon />,
                    name: 'Quitter Mode Édition',
                    to: paths.consult,
                  },
                ]
              : [
                  {
                    id: 'editMissionServiceUsages',
                    icon: <EditIcon />,
                    isDisabled: !isPricingEditionModeAvailable({
                      isOptionGroupsSelectionEditable,
                      isPricingEditable: pricingEditionPermission.isEditable,
                      source,
                    }),
                    name: 'Modifier Chiffrage',
                    to: paths.edit,
                  },
                ]
          }
          title="Actions possibles sur le Chiffrage"
        />
      )}
    </PricingEditionContextProvider>
  );
};
